/**
 * Utilities for translating structured backend import error messages (KAN-259).
 *
 * The backend emits machine-readable errors prefixed with "IMPORT_ERR:" followed
 * by a JSON payload: IMPORT_ERR:{"code":"...","row":1,...}
 *
 * Known error codes and their i18n keys:
 *   DUPLICATE_LEGACY_REF1_IN_DB   → commodities.tradeImport.errors.duplicateLegacyRef1InDb
 *   DUPLICATE_LEGACY_REF1_IN_FILE → commodities.tradeImport.errors.duplicateLegacyRef1InFile
 *
 * Any unrecognised error string (no prefix or unknown code) is returned as-is.
 */

import { TFunction } from 'i18next';

const PREFIX = 'IMPORT_ERR:';

/** Convert SCREAMING_SNAKE_CASE to camelCase for i18n key lookup. */
function toCamelCase(code: string): string {
  return code.toLowerCase().replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

/**
 * Translate a single import error string.
 * Returns the original string if it is not a structured IMPORT_ERR message.
 */
export function translateImportError(error: string, t: TFunction): string {
  if (!error.startsWith(PREFIX)) return error;
  try {
    const payload = JSON.parse(error.slice(PREFIX.length));
    const { code, ...params } = payload as { code: string; [key: string]: unknown };
    const key = `commodities.tradeImport.errors.${toCamelCase(code)}`;
    return t(key, params as Record<string, unknown>);
  } catch {
    return error;
  }
}
